/**
 * Defines the user dialog options and utilities for using them
 * Any functions that reference specific id's for objects are defined here
 * Make sure to do a ctrl-f before changing an ID name
 */

/**
 * Uses a recursive definition of the branching structure to make it simple to change the pattern
 */
interface PromptObj {
  percentage: number; // show the progress of the wizard
  prompt: string; //text prompt to give context to the following options
  singleSelect: boolean; // whether to limit the option to one
  defaultSelection: string[];
  options: OptionBranch[];
}

interface OptionBranch {
  id: string; // these may be referenced by functions inside the file
  displayName: string;
  disabled: boolean; //whether to disable the checkbox on the frontend
  branch: PromptObj[]; // these define more prompts to show the user if this option is selected
}

const socialProviders = ['Google', 'Facebook', 'LinkedIn'];
const socialProviderSelectionBranch = {
  prompt: 'Please select which social account providers to enable',
  singleSelect: false,
  defaultSelection: ['google'],
  percentage: 90,
  options: socialProviders.map((prov) => {
    return {
      id: prov.toLowerCase(),
      displayName: prov,
      disabled: false,
      branch: [],
    };
  }),
};

const accountAndPwdBranch = {
  prompt:
    'Would you like to allow users to sign in with a socal account and a password? (I.E. If a user registers with foo@gmail.com, they could use their password or their Google account)',
  singleSelect: true,
  defaultSelection: ['idkSocialPwdSignIn'],
  percentage: 80,
  options: [
    {
      id: 'allowSocialPwdSignIn',
      displayName: 'Yes',
      branch: [],
      disabled: false,
    },
    {
      id: 'noSocialPwdSignIn',
      displayName: 'No',
      branch: [],
      disabled: false,
    },
    {
      id: 'idkSocialPwdSignIn',
      displayName: "I don't know",
      branch: [],
      disabled: false,
    },
  ],
};

// maybe add this as a seperate option for howSignInBranch?
const socialProvidersBranch = {
  prompt:
    'Do you plan to support outside account providers for authentication? (I.E. The user will see a "Sign in with X" option--you will be asked which providers later)',
  singleSelect: true,
  defaultSelection: ['yesSocialProviders'],
  percentage: 75,
  options: [
    {
      id: 'yesSocialProviders',
      displayName: 'Yes',
      disabled: false,
      branch: [socialProviderSelectionBranch, accountAndPwdBranch],
    },
    {
      id: 'noSocialProviders',
      displayName: 'No',
      disabled: false,
      branch: [],
    },
  ],
};

const passwordHashOption = {
  prompt: 'Which hashing alogrithm are you planning to use?',
  singleSelect: true,
  defaultSelection: ['sha256'],
  percentage: 70,
  options: [
    {
      id: 'sha256',
      displayName: 'SHA256',
      disabled: false,
      branch: [],
    },
    {
      id: 'sha1',
      displayName: 'SHA1',
      disabled: false,
      branch: [],
    },
    {
      id: 'customHash',
      displayName: 'Custom hashing algorithm',
      disabled: false,
      branch: [],
    },
    {
      id: 'idkHash',
      displayName: "I don't know",
      disabled: false,
      branch: [],
    },
  ],
};

const passwordMigrationOption = {
  prompt: 'How would you like to handle stored passwords',
  singleSelect: true,
  defaultSelection: ['migratePassword'],
  percentage: 65,
  options: [
    {
      id: 'migratePassword',
      displayName: 'Migrate stored passwords',
      disabled: false,
      branch: [passwordHashOption],
    },
    {
      id: 'forceResetPassword',
      displayName: 'Ask users to reset passwords on next login',
      disabled: false,
      branch: [],
    },
  ],
};

const migrationBranch = {
  prompt: 'Are you migrating identities from an existing source (IdP)?',
  singleSelect: true,
  defaultSelection: ['migrateIdP'],
  percentage: 60,
  options: [
    {
      id: 'migrateIdP',
      displayName: 'Yes',
      disabled: false,
      branch: [passwordMigrationOption],
    },
    {
      id: 'noMigrateIdP',
      displayName: 'No',
      disabled: false,
      branch: [],
    },
  ],
};

const howSignInBranch = {
  prompt: 'How would you like your users to sign in?',
  singleSelect: false,
  defaultSelection: ['emailPwdSocial'],
  percentage: 50,
  options: [
    {
      id: 'emailPwdSocial',
      displayName: 'Email and password, or an account provider',
      branch: [socialProvidersBranch, migrationBranch],
      disabled: false,
    },
    {
      id: 'otp',
      displayName: 'One time password sent to email or phone number',
      branch: [],
      disabled: false,
    },
    {
      id: 'biometrics',
      displayName: 'Biometric sign in (E.G. fingerprint or facial recognition)',
      branch: [],
      disabled: false,
    },
  ],
};

const mfaBranch = {
  prompt: 'Would you like to require multifactor authentication everytime a user signs in?',
  singleSelect: true,
  defaultSelection: ['disableMFA'],
  percentage: 40,
  options: [
    {
      id: 'enableMFA',
      displayName: 'Yes',
      branch: [],
      disabled: false,
    },
    {
      id: 'disableMFA',
      displayName: 'No',
      branch: [],
      disabled: false,
    },
  ],
};

const conditionalAccessBranch = {
  prompt:
    'Conditional Access - Conditional Access is a feature from Microsoft that detects sign in threats in real time. If a threat is detected the user is asked to do a MFA. If no threat is detected MFA does not happen. There is additional licensing for this. Do you want enable this feature?',
  singleSelect: true,
  defaultSelection: ['idkConditionalAccess'],
  percentage: 30,
  options: [
    {
      id: 'enableConditionalAccess',
      displayName: 'Yes',
      disabled: false,
      branch: [],
    },
    {
      id: 'disableConditionalAccess',
      displayName: 'No',
      disabled: false,
      branch: [mfaBranch],
    },
    {
      id: 'idkConditionalAccess',
      displayName: "I don't know",
      disabled: false,
      branch: [mfaBranch],
    },
  ],
};

const howSignUpBranch = {
  prompt: 'How would you like your users to sign up?',
  singleSelect: false,
  defaultSelection: ['invitedSignUp'],
  percentage: 20,
  options: [
    {
      id: 'selfServiceSignUp',
      displayName: 'Self service sign up',
      disabled: false,
      branch: [],
    },
    {
      id: 'invitedSignUp',
      displayName: 'I plan to invite my users',
      disabled: false,
      branch: [],
    },
  ],
};

const newJourneyOption = {
  id: 'startNew',
  displayName: 'Create a new user journey',
  disabled: false,
  branch: [howSignInBranch, conditionalAccessBranch, howSignUpBranch],
};

const dialogOptions = [
  {
    prompt: 'Welcome to the Grit IEF policy editor. What do you plan to do?',
    singleSelect: true,
    defaultSelection: ['startNew'],
    percentage: 10,
    options: [
      {
        id: 'editExisting',
        displayName: 'Edit existing user journey XML',
        disabled: false,
        branch: [],
      },
      newJourneyOption,
    ],
  },
];

function optionInSelectionList(selection: string[], prompt: PromptObj) {
  return selection.findIndex((sel) => prompt.options.findIndex((opt) => sel === opt.id) !== -1) !== -1;
}

function _getNextOption(optionStack: any[], currentSelections: string[]) {
  if (optionStack.length < 1) {
    return null;
  }
  let returnValue: PromptObj | null = null;
  optionStack.forEach((promptObj: PromptObj) => {
    //check if options under this prompt are in current selections
    const indexes = [];
    for (let i = 0; i < promptObj.options.length; i++) {
      if (currentSelections.findIndex((sel) => sel === promptObj.options[i].id) !== -1) {
        indexes.push(i);
      }
    }
    indexes.forEach((ind) => {
      const nextBranch = promptObj.options[ind].branch || [];
      returnValue = _getNextOption(nextBranch, currentSelections) || returnValue;
    });
    if (!indexes.length) {
      returnValue = promptObj;
    }
  });
  return returnValue;
}

function getNextPrompt(currentSelections: string[]) {
  /**
   * Returns the next prompt for the user to fill out.
   * currentSelections is a list of all option ids that the user has previously selected
   * make sure to include all options selected from all previous prompts
   */
  const nextPrompt = _getNextOption(dialogOptions, currentSelections);

  return nextPrompt;
}

export function createGeneratorOptions(currentSelections: string[]) {
  const generatorOptions = {
    passwordMigration: {
      selectedOption: currentSelections.find((sel) => sel === 'forceResetPassword')
        ? 'resetPassword'
        : 'migratePasswords',
    },
    social: {
      google: {
        value: currentSelections.find((sel) => sel === 'google') !== undefined,
        linkAccounts: currentSelections.find((sel) => sel === 'allowSocialPwdSignIn') !== undefined,
      },
    },
    otp: {
      choiceBetweenEmailPhone: {
        value: currentSelections.find((sel) => sel === 'otp') !== undefined,
      },
    },
    conditionalAccess: {
      value: currentSelections.find((sel) => sel === 'enableConditionalAccess') !== undefined,
    },
    biometric: {
      value: currentSelections.find((sel) => sel === 'biometrics') !== undefined,
    },
    mfa: {
      value: currentSelections.find((sel) => sel === 'enableMFA') !== undefined,
    },
  };
  return generatorOptions;
}

interface UnsupportedOption {
  prompt: string;
  optionName: string;
}

function findOptionPromptAndDisplayName(optionId: string, topPrompt: PromptObj) {
  const optionStack = topPrompt.options;
  let returnValue: UnsupportedOption | null = null;
  optionStack.forEach((option: OptionBranch) => {
    if (optionId === option.id) {
      returnValue = {
        prompt: topPrompt.prompt,
        optionName: option.displayName,
      };
    } else {
      option.branch.forEach((prompt) => {
        returnValue = findOptionPromptAndDisplayName(optionId, prompt) || returnValue;
      });
    }
  });
  return returnValue;
}

const UNSUPPORTEDOPTIONIDS = ['facebook', 'linkedin', 'sha1', 'customHash'];
export function getUnsupportedOptions(currentSelections: string[]) {
  const unsupportedOptions: UnsupportedOption[] = [];
  UNSUPPORTEDOPTIONIDS.forEach((option) => {
    if (currentSelections.find((selection) => selection === option)) {
      const newOpt = findOptionPromptAndDisplayName(option, dialogOptions[0]);
      if (newOpt) {
        unsupportedOptions.push(newOpt);
      }
    }
  });
  return unsupportedOptions;
}

const archIdsAndMessages = [
  {
    id: 'invitedSignUp',
    text: 'An external service is needed to construct the invitation email and send them to the users. Please consult our documentation on more details about it.',
  },
  {
    id: 'migratePassword',
    text: 'When migrating the passwords, the hashed data along with salt is migrated to AAD B2C. The very first time the user enters a password , the plain text password is hashed using the same algorithm as before. The hashed password is compared against the stored password. If a match is found, the plain text password entered by the user is stored in AAD B2C.',
  },
  {
    id: 'yesSocialProviders',
    text: "You will have to create accounts in social providers representing your organization. For this POC Grit's applications are being used.",
  },
  {
    id: 'otp',
    text: 'For one time passwords, the user will be sent a 6 digit code to their email/phone. The code is valid for 5 minutes. These can be configured later.',
  },
  {
    id: 'enableConditionalAccess',
    text: 'You can define custom policies for threats on top of what is provided by Microsoft. Those are not configurable in this POC.',
  },
];

export function getArchitecturalMessage(selections: string[]) {
  const messages: string[] = [];
  selections.forEach((selection) => {
    const message = archIdsAndMessages.find((mesObj) => mesObj.id === selection);
    if (message) {
      messages.push(message.text);
    }
  });
  return messages;
}

export function shouldCreateNewJourney(selections: string[]) {
  /**
   * Returns a boolean telling if you should create a new user journey given user selections
   * We're defining the function here, so if the id's change, it's only in one file
   */
  return selections.includes('startNew');
}

export function shouldUploadPolicy(selections: string[]) {
  /**
   * Returns a boolean telling if you should take the user to the upload screen after completion
   * We're defining the function here, so if the id's change, it's only in one file
   */
  return selections.includes('editExisting') && !selections.includes('startNew');
}

export default getNextPrompt;
export type { PromptObj, OptionBranch, UnsupportedOption };
